const ORDER_ITEM_FIELDS = ['items', 'returns'];
const EXCHANGE_FIELDS = ['items', 'returns', 'haulaway_items'];
const ORDER_ITEM_RELATION = ['itemsByOrderId'];

// Walks every (order, field, item) triple and folds the surviving items into the accumulator.
// Intangible items are informational only (no physical footprint), so they are skipped here —
// this is the single place that exclusion is enforced for every aggregate below. `field` is
// passed through so callers can bucket by the collection an item came from.
const reduceItems = (orders, fields, reducer, initial) => {
    let acc = initial;
    (orders || []).forEach((order) => {
        fields.forEach((field) => {
            (order?.[field] || []).forEach((item) => {
                if (item.intangible) return;
                acc = reducer(acc, item, field);
            });
        });
    });
    return acc;
};

// Reads an attribute as-is, preserving the arithmetic of the hand-rolled reduces these helpers
// replaced — including NaN propagation when a summed field is absent.
const rawAttr = (name) => (item) => item[name];

// Coerces to a finite number so table sorting and rendered totals never produce NaN.
const numericAttr = (name) => (item) => {
    const value = Number(item?.[name]);
    return Number.isFinite(value) ? value : 0;
};

// Sums one value per surviving item. `value` is the extractor, so call sites that need their own
// coercion (ranges, per-field fallbacks) can supply it without restating the intangible rule.
export const sumOrderItems = (orders, { fields = ORDER_ITEM_FIELDS, value, where }) =>
    reduceItems(orders, fields, (total, item) => (where && !where(item) ? total : total + value(item)), 0);

export const calcItemCount = (orders) => sumOrderItems(orders, { value: rawAttr('quantity') });

export const calcAnyItemTypeCount = (items) => {
    if (items?.length > 0) {
        return items.reduce((total, item) => {
            let count = 0;
            count += item.quantity;
            return total + count;
        }, 0);
    }
    return 0;
};

export const calcItemWeight = (orders) => sumOrderItems(orders, { value: rawAttr('total_weight') });

export const calcItemVolume = (orders) => sumOrderItems(orders, { value: rawAttr('total_cubes') });

export const calcItemVolumeSubscription = (orders) =>
    sumOrderItems(orders, { fields: ORDER_ITEM_RELATION, value: rawAttr('total_cubes') });

export const calcItemWeightSubscription = (orders) =>
    sumOrderItems(orders, { fields: ORDER_ITEM_RELATION, value: rawAttr('total_weight') });

const haulawayItemCubes = (item) =>
    item?.cubes_per_unit
        ? item.cubes_per_unit * item.quantity
        : ((item.height * item.width * item.length) / 1728) * item.quantity;

export const calcExchangeVolumes = (orders) => {
    // Callers rely on a scalar 0 rather than a zeroed bucket object when there are no orders.
    if (!(orders?.length > 0)) {
        return 0;
    }

    return reduceItems(
        orders,
        EXCHANGE_FIELDS,
        (totalCubes, item, field) => {
            if (field === 'returns') {
                totalCubes.returnsCubes += item.total_cubes;
            } else if (field === 'items') {
                totalCubes.itemsCubes += item.total_cubes;
            } else {
                totalCubes.haulawayCubes += haulawayItemCubes(item);
            }
            return totalCubes;
        },
        { returnsCubes: 0, itemsCubes: 0, haulawayCubes: 0 }
    );
};

// Aggregates the itemsByOrderId relation for surfaces that never receive the pre-split
// items/returns arrays. `where` narrows to delivery or return lines via item.is_return.
export const calcOrderItemTotal = (orders, name, where) =>
    sumOrderItems(orders, { fields: ORDER_ITEM_RELATION, value: numericAttr(name), where });

export const isReturnLine = (item) => !!item?.is_return;
export const isDeliveryLine = (item) => !item?.is_return;
